/**
 * SOW Format Spec - the format extractor's typed output (and the eval golden's target)
 * ────────────────────────────────────────────────────────────────────────────────────
 * Given example SOW .docx files, the format extractor produces a `SowFormatSpec`: an
 * ordered list of `SectionSpec`s, each tagged with a `SectionRole` (the SECTION ROLE
 * taxonomy, orthogonal to @protoss/doc's `LogicalContentType` which is the CONTENT type),
 * plus the per-section tokens / data-binding / verbatim flags and the document's visual
 * identity. This is distinct from the render-time `DocumentContract`: the spec is what the
 * authoring agent reasons about and what a hand-labeled `SowFormatGolden` is scored against.
 *
 * Design grounded in the real corpus (RCP NetSuite SOWs + Echo Park Rillet template):
 *   - Section keys must be COLLISION-PROOF. Echo Park repeats "objective"/"key_activities"/
 *     "deliverables" under every phase, so a bare slug is ambiguous. {@link sectionFormatKeys}
 *     derives a stable per-document key (bare slug when unique, else `slug__2`, and a
 *     synthetic key for blank headings) from the ordered fragment list; the extractor and the
 *     golden compute keys identically so they align.
 *   - Sections can be OPTIONAL/conditional ("<Remove whole section if not doing Maxio>") and
 *     carry EDITORIAL authoring notes embedded in headings/body - distinct from value tokens.
 *   - Confidence is three-valued (`high|medium|low`); the alpha's binary high|low could not
 *     express "evidence conflicts".
 *
 * Kept PERMISSIVE (no cross-field `.refine()`) so it can drive `generateValidated`
 * self-correction the same way the reference-estimate schema does.
 */
import { z } from 'zod';
import { slugifyHeading } from '../utils/sow-sections.js';
/**
 * The SECTION ROLE taxonomy - WHERE a section's content comes from at deal/render time.
 * Orthogonal to content TYPE (paragraph/table/...). One role per section.
 *
 *  - cover_chrome   Title page, header/footer bands, page styling, logos, watermark. Extracted
 *                   deterministically from style + visual-identity slots; never model-authored.
 *  - container      Pure structural grouping heading (empty/near-empty body, groups child
 *                   sections), e.g. the Echo Park phase headings "Plan" / "Migrate & Reconcile".
 *  - fixed          Boilerplate prose near-identical across deals; copied verbatim, no tokens.
 *  - hybrid         Mostly-fixed prose with a few inline value tokens (client name, dates, $).
 *  - data_sourced   A table rebuilt at render from deal/estimate/resource-plan DB data (dataKey).
 *  - ai_generated   Per-deal narrative produced by the SOW generator at deal time.
 *  - user_populated Deal-specific literal values a human types (project name, SOW#, signers).
 *  - signature      Signature / acceptance block (parties, e-sign anchors).
 *  - toc            Table of contents (live field in Word, static list in Google Docs).
 */
export const SECTION_ROLES = [
    'cover_chrome',
    'container',
    'fixed',
    'hybrid',
    'data_sourced',
    'ai_generated',
    'user_populated',
    'signature',
    'toc',
];
export const sectionRoleSchema = z.enum(SECTION_ROLES);
/** Where a value token resolves from. `editorial` notes are NOT tokens - see SectionSpec.editorialNote. */
export const TOKEN_SOURCES = ['system', 'user', 'deal'];
/** One value-injection point inside a hybrid/user_populated section. */
export const tokenRefSchema = z.object({
    /** The token as it would appear in the template, e.g. `{{client.legalName}}` or the raw span text. */
    token: z.string(),
    source: z.enum(TOKEN_SOURCES).default('system'),
    /** The deal/system context key this token resolves from, when known (e.g. `context.clientName`). */
    contextKey: z.string().optional(),
    /** Verbatim canonical text the token replaces (the span), for round-trip + reviewer display. */
    spanText: z.string().optional(),
});
/** Cross-document recurrence evidence the classifier reasons over (drives confidence + role). */
export const sectionEvidenceSchema = z.object({
    /** Mean pairwise body similarity across the docs the section appears in, 0..1. */
    similarity: z.number().min(0).max(1).optional(),
    /** How many example docs this section's heading was found in. */
    presentInDocs: z.number().int().nonnegative().optional(),
    /** Total example docs considered. */
    totalDocs: z.number().int().nonnegative().optional(),
    /** Whether the parsed section body contains at least one table. */
    hasTable: z.boolean().default(false),
    /** Body character count (0 ⇒ heading-only / container). */
    charCount: z.number().int().nonnegative().optional(),
});
/** One extracted SOW section, fully classified. */
export const sectionSpecSchema = z.object({
    /** Stable, collision-proof key within the document (see {@link sectionFormatKeys}). */
    key: z.string(),
    /** The slug of the heading (may collide across the doc; `key` disambiguates). */
    slug: z.string(),
    /** Verbatim heading text as parsed (load-bearing - never paraphrased by the model). */
    headingText: z.string(),
    /** Heading depth 1..6 (0 for preamble). */
    level: z.number().int().min(0).max(6).default(1),
    role: sectionRoleSchema,
    confidence: z.enum(['high', 'medium', 'low']).default('medium'),
    evidence: sectionEvidenceSchema.optional(),
    /** Value tokens detected in this section (hybrid/user_populated). Empty for fixed/ai/data. */
    tokens: z.array(tokenRefSchema).default([]),
    /** For data_sourced: the binding catalog key the table rebuilds from (e.g. `estimate_grid`). */
    dataKey: z.string().optional(),
    /** For ai_generated: the narrative slot key the generator fills. */
    narrativeKey: z.string().optional(),
    /** True ⇒ the body must be preserved byte-for-byte (fixed sections). */
    verbatim: z.boolean().default(false),
    /** True ⇒ section is conditional ("remove if ... out of scope"). */
    optional: z.boolean().default(false),
    /** Authoring instruction extracted from the heading/body (e.g. "Remove section if Maxio out of scope"). */
    editorialNote: z.string().optional(),
    /** Model/judge rationale for the role choice (audit trail). */
    rationale: z.string().optional(),
    /** Judge verdict when the self-eval pass ran ('pass' | a reason it disagreed). */
    judgeVerdict: z.string().optional(),
});
/** A detected visual/styling slot (cover/header/footer asset or page-styling cue). */
export const visualSlotSchema = z.object({
    location: z.enum(['cover', 'header', 'footer', 'body', 'background', 'watermark']),
    /** Asset slot id when this is an image slot (resolves to a uuid at export). */
    slot: z.string().optional(),
    kind: z.enum(['logo', 'image', 'text', 'styling']).default('image'),
    note: z.string().optional(),
});
export const visualIdentitySchema = z.object({
    defaultFont: z.string().optional(),
    bodySize: z.number().optional(),
    hasCoverPage: z.boolean().default(false),
    hasToc: z.boolean().default(false),
    slots: z.array(visualSlotSchema).default([]),
});
/** The format extractor's full output for one set of example documents. */
export const sowFormatSpecSchema = z.object({
    schemaVersion: z.literal(1).default(1),
    /** The example doc the spec's structure/styling was taken from (the canonical). */
    canonicalAssetUuid: z.string().optional(),
    productKey: z.string().optional(),
    categoryKey: z.string().optional(),
    sections: z.array(sectionSpecSchema).default([]),
    visualIdentity: visualIdentitySchema.optional(),
    /** Non-fatal extraction warnings surfaced to the admin (heading recovery, blank sections, etc.). */
    warnings: z.array(z.string()).default([]),
});
// ───────────────────────────── Golden (hand-labeled ground truth) ─────────────────────────────
/** One hand-labeled golden section. A relaxed/subset view of SectionSpec - only the scored fields. */
export const goldenSectionSchema = z.object({
    /** Stable key (matches {@link sectionFormatKeys}). The join key against the extractor output. */
    key: z.string(),
    slug: z.string(),
    headingText: z.string(),
    role: sectionRoleSchema,
    /** Expected token context-keys (or raw token strings) for hybrid/user_populated sections. */
    tokens: z.array(z.string()).default([]),
    /** Expected binding catalog key for data_sourced sections. */
    dataKey: z.string().optional(),
    optional: z.boolean().default(false),
    /** True ⇒ the labeler expects byte-identical body preservation (fixed). */
    verbatim: z.boolean().default(false),
    /** Labeler confidence/judgment-call flag - sections marked uncertain are excluded from hard gates. */
    uncertain: z.boolean().default(false),
    note: z.string().optional(),
});
export const sowFormatGoldenSchema = z.object({
    /** Source .docx filename (for the report). */
    docFile: z.string(),
    /** Vendor/methodology label, e.g. 'rcp-netsuite' | 'echo-park-rillet' (cross-methodology genericity). */
    methodology: z.string().optional(),
    sections: z.array(goldenSectionSchema).default([]),
});
// ───────────────────────────── Stable key derivation ─────────────────────────────
/**
 * Derive stable, collision-proof keys for an ordered list of parsed sections. Both the extractor
 * and the golden call this over the SAME parsed fragment order so their keys join 1:1.
 *
 * Rules: the first occurrence of a slug keeps the bare slug; later occurrences get `slug__2`,
 * `slug__3`, … ; a blank/empty slug (parser noise from page-break artifacts) becomes
 * `__blank_<index>`. Order-derived, deterministic, and independent of role classification.
 */
export function sectionFormatKeys(slugs) {
    const counts = new Map();
    return slugs.map((s, i) => {
        const base = s.slug && s.slug.trim() ? s.slug : `__blank_${i}`;
        const n = (counts.get(base) ?? 0) + 1;
        counts.set(base, n);
        return n === 1 ? base : `${base}__${n}`;
    });
}
/** Convenience: key a single heading the same way the slug chain does (for ad-hoc lookups). */
export function formatKeyForHeading(heading) {
    return slugifyHeading(heading);
}
/**
 * Normalize a section body for VERBATIM comparison (R5). The docx parse → reparse round-trip
 * legitimately reflows whitespace and run boundaries, so "byte-identical" is compared on
 * collapsed, trimmed, lowercased text - not raw OOXML - or the rule false-fails.
 */
export function normalizeVerbatim(text) {
    return text
        .replace(/ /g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}
//# sourceMappingURL=sow-format-spec.js.map